import React, { useContext } from 'react'

import { ItemCardVariant } from '../ItemCard'
import ItineraryItemCard from '../ItineraryItemCard'
import { PlannedItemCtaContent } from '../PlannedItemCtaContent'

import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import GeoContext from 'contexts/geoContext'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { TourV2VariationDetails } from 'lib/TourV2/extractTourV2VariationDetails'
import {
  TourV2OfferCardPriceDetails,
  getItineraryDaysCount,
} from 'tripPlanner/components/Bookmark/BookmarkCard/TourV2Card'
import BookmarkLocation from 'tripPlanner/components/Bookmark/BookmarkCardNew/BookmarkLocation'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import TripContext from 'tripPlanner/contexts/TripContext'
import useTourV2ItemConfirmationCodes from 'tripPlanner/hooks/confirmationCodes/useTourV2ItemConfirmationCodes'
import useTourV2ItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useTourV2ItemOccupancyDescription'
import { TourItem } from 'tripPlanner/types/tripItem'
import {
  buildMyEscapesOrderLink,
  formatDatesAndDays,
  formatDurationDaysIfMultipleDays,
  formatStartAndEndLocation,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/deepLink'

interface Props {
  item: TourItem
  offer: Tours.V2Offer
  onClickCard: (offerLink: string) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
}

export default function TourV2ItemCard({
  offer,
  item,
  onClickCard,
  onClickTripName,
  variant,
}: Props) {
  const {
    cheapestPurchasableOption: purchasableOption,
    cheapestVariation: variation,
  } = useCheapestOfTourV2Offer(offer)
  const variationDetails: TourV2VariationDetails =
    useTourV2VariationDetails(variation)

  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate
  const canEdit = tripContext?.permissions.canEdit ?? false

  const { currentCurrency } = useContext(GeoContext)

  const timeFormat = useRegionTimeFormat()

  const bookingId = useTourV2ItemConfirmationCodes(item)
  const travellersDescription = useTourV2ItemOccupancyDescription(item)

  const productTypeLabel = offer ? (
    <ProductTypeLabel productType={offer.productType} />
  ) : null

  if (!purchasableOption || !variation || !variationDetails) {
    return null
  }
  const {
    startLocation,
    endLocation,
    hasTheSameStartAndEndLocation,
    inclusionItems,
  } = variationDetails

  const locationDescription = (
    <BookmarkLocation
      startLocation={startLocation}
      endLocation={hasTheSameStartAndEndLocation ? startLocation : endLocation}
      variant="start_and_end"
      showIcon={false}
    />
  )
  const image = variation.images[0]

  const timeDescription = formatDatesAndDays(item, timeFormat)
  const durationDescription = formatDurationDaysIfMultipleDays(item)

  if (isLeBookmark(item)) {
    const tileLink = buildProductLink(item, !viewAsTemplate, { offer }) ?? ''

    const priceAreaContent = (
      <TourV2OfferCardPriceDetails
        depositComponent={null}
        purchasableOption={purchasableOption}
        duration={getItineraryDaysCount(variation)}
        currencyCode={currentCurrency}
      />
    )
    const ctaAreaContent = (
      <PlannedItemCtaContent
        item={item}
        isOfferAvailable
        offerId={item.code}
        checkIn={item.startDate}
        checkOut={item.endDate}
        variant={variant}
      />
    )

    const inclusions: App.TileInclusion[] | undefined = inclusionItems?.map(
      (inclusion) => ({
        text: inclusion.inclusionBody,
        isHighlighted: false,
        icon: inclusion.categoryIcon.split(',')[0], // Sometimes it's coming through as 'car, Transport'
      }),
    )
    return (
      <SavedOfferCard
        item={item}
        name={item.name}
        imgId={image?.id}
        imgSrc={image?.url}
        imageOverlayContent={productTypeLabel}
        locationDescription={locationDescription}
        descriptors={[timeDescription, durationDescription]}
        printDescriptors={[
          timeDescription,
          durationDescription,
          travellersDescription,
        ]}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        onClick={() => onClickCard(tileLink)}
        testId="saved-tour-card"
        onClickTripName={onClickTripName}
        variant={variant}
        inclusions={inclusions}
        offerURL={tileLink}
      />
    )
  } else {
    const myEscapesLink =
      canEdit && !viewAsTemplate ? buildMyEscapesOrderLink(item) : undefined
    return (
      <ItineraryItemCard
        item={item}
        name={item.name}
        imageOverlayContent={productTypeLabel}
        locationDescription={formatStartAndEndLocation(item)}
        descriptors={[timeDescription, durationDescription]}
        printDescriptors={[
          timeDescription,
          durationDescription,
          travellersDescription,
        ]}
        confirmationCode={bookingId.codeString}
        confirmationCodeLabel={bookingId.label}
        onClickTripName={onClickTripName}
        variant={variant}
        onClick={myEscapesLink ? () => onClickCard(myEscapesLink) : undefined}
      />
    )
  }
}
